import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { LocationSwitcherService, Location, RecentLocation } from '../../services/location-switcher/location-switcher.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-location-switcher',
  templateUrl: './location-switcher.component.html',
  styleUrls: ['./location-switcher.component.scss']
})
export class LocationSwitcherComponent implements OnInit, OnDestroy {
  
  private destroy$ = new Subject<void>();
  
  currentLocation: Location | null = null;
  recentLocations: RecentLocation[] = [];
  allLocations: Location[] = [];
  filteredLocations: Location[] = [];
  isLoading = false;
  selectedLocation: Location | null = null;

  constructor(
    private locationSwitcherService: LocationSwitcherService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.subscribeToLocationData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToLocationData(): void {
    // Subscribe to current location
    this.locationSwitcherService.currentLocation$
      .pipe(takeUntil(this.destroy$))
      .subscribe(location => {
        this.currentLocation = location;
      });

    // Subscribe to recent locations
    this.locationSwitcherService.recentLocations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(locations => {
        this.recentLocations = locations;
      });

    // Subscribe to all locations
    this.locationSwitcherService.allLocations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(locations => {
        this.allLocations = locations;
        this.filteredLocations = [...locations]; // Create a copy
      });

    // Subscribe to loading state
    this.locationSwitcherService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });
  }

  onLocationSearch(event: any): void {
    const query = event.query;
    
    // For dropdown mode, when query is empty, show all locations
    if (query === '' || query === null || query === undefined) {
      this.filteredLocations = [...this.allLocations];
    } else {
      this.filteredLocations = this.locationSwitcherService.filterLocations(query);
    }
  }

  async onLocationSelected(location: Location): Promise<void> {
    if (!location || !location.id) {
      return;
    }

    try {
      const success = await this.locationSwitcherService.switchToLocation(location.id);
      
      if (success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Location Switched',
          detail: `Successfully switched to ${location.name}`
        });
        
        // Clear the selected location in the autocomplete
        this.selectedLocation = null;
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Switch Failed',
          detail: 'Failed to switch location. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error switching location:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred while switching location.'
      });
    }
  }

  async onRecentLocationClick(recentLocation: RecentLocation): Promise<void> {
    const location: Location = {
      id: recentLocation.id,
      name: recentLocation.name
    };
    
    await this.onLocationSelected(location);
  }

  onRefresh(): void {
    this.locationSwitcherService.refreshLocations();
  }

  // Custom display function for the autocomplete
  getLocationDisplay(location: Location): string {
    return location ? `${location.name} (${location.id})` : '';
  }

  // Check if a recent location is the current location
  isCurrentLocation(recentLocation: RecentLocation): boolean {
    return this.currentLocation?.id === recentLocation.id;
  }

  // Get the current location display text
  getCurrentLocationDisplay(): string {
    if (!this.currentLocation) {
      return 'No location selected';
    }
    
    return `${this.currentLocation.name} (${this.currentLocation.id})`;
  }
}